import React from 'react';
import numeral from 'numeral';

import LoadingSpinner, {
  SIZES
} from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import SellsideConsensus from 'buyside/components/presentation/SellsideConsensus/sellsideConsensus';

import { useEntityStatsQuery } from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/CompanyShowPage/companyShowPageStyles.scss';

const EntityShowStats: React.FC<{ id: string }> = ({ id }): React.ReactElement => {
  const { data, loading, error } = useEntityStatsQuery({ variables: { id } });

  if (loading) return <LoadingSpinner size={SIZES.MEDIUM} />;

  if (error) throw error;

  if (
    !data?.entity?.primaryAsset?.companyData ||
    !data?.entity?.primaryAsset?.currencyCode ||
    !data?.entity?.primaryAsset?.fundamental
  ) {
    return (
      <div className={styles.accordionInner}>
        <span className={styles.smallText}>No data</span>
      </div>
    );
  }

  const {
    companyData: { country, geography, industry, sector },
    currencyCode,
    fiftyTwoWeekRange,
    fundamental: {
      bookValue,
      currentPe,
      debtBook,
      divYield,
      ev,
      evEbitda,
      evFcf,
      evSales,
      fcfYield,
      forwardPe,
      marketCap,
      priceBook,
      sellsideConsensus,
      threeMonthAdv,
      totalCash,
      totalDebt
    }
  } = data.entity?.primaryAsset;

  return (
    <div className={styles.stats}>
      <h5>Summary</h5>
      {[
        { label: 'Sector', value: sector },
        { label: 'Industry', value: industry },
        { label: 'Country', value: country },
        { label: 'Region', value: geography }
      ].map(pair => (
        <div key={pair.label}>
          <span>{pair.label}</span>
          <span>{pair.value || 'N/A'}</span>
        </div>
      ))}
      <h5>Fundamentals</h5>
      {[
        { label: 'Currency', value: currencyCode },
        {
          label: `52 Wk. Range ${currencyCode ? `(${currencyCode})` : ''}`,
          value: fiftyTwoWeekRange
        },
        { label: 'Market Cap', value: `${numeral(marketCap).format('0.0a')} USD` },
        { label: 'EV', value: `${numeral(ev).format('0.0a')} USD` },
        { label: 'Total Cash', value: `${numeral(totalCash).format('0.0a')} USD` },
        { label: 'Total Debt', value: `${numeral(totalDebt).format('0.0a')} USD` },
        { label: 'Book Value Per Share', value: bookValue },
        { label: '3 Mo. Avg. Daily Vol', value: `${numeral(threeMonthAdv).format('0,0')}` }
      ].map(pair => (
        <div key={pair.label}>
          <span>{pair.label}</span>
          <span>{pair.value || 'N/A'}</span>
        </div>
      ))}
      <div className={styles.ssConsensus}>
        <span>Sellside Consensus</span>
        <span>
          <SellsideConsensus sellsideConsensus={sellsideConsensus} />
        </span>
      </div>
      <h5>Multiples/Ratios</h5>
      {[
        { label: 'LTM/PE', value: currentPe },
        { label: 'Forward PE', value: forwardPe },
        { label: 'EV/EBITDA', value: evEbitda },
        { label: 'EV/FCF', value: evFcf },
        { label: 'EV Sales', value: evSales },
        { label: 'Price Book', value: priceBook },
        { label: 'FCF Yield:', value: fcfYield },
        { label: 'Debt Book', value: debtBook },
        { label: 'Div. Yield', value: divYield }
      ].map(pair => (
        <div key={pair.label}>
          <span>{pair.label}</span>
          <span>{pair.value || 'N/A'}</span>
        </div>
      ))}
    </div>
  );
};

export default EntityShowStats;
